import React, { useState, useEffect } from "react";
import {
    User as UserIcon, Mail, Calendar, Shield, CreditCard,
    Save, Loader2, CheckCircle2, AlertCircle, Chrome
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Sidebar, Header, ToastContainer } from "../Dashboard/DashboardComponents";
import { authService } from "../../services/auth.service";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const Profile = () => {
    const [user, setUser] = useState<any>(null);
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();

    const showToast = (type: "success" | "error", msg: string) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 3500);
    };

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 1024);
            setSidebarOpen(window.innerWidth >= 1024);
        };
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        authService.getProfile()
            .then((u) => { setUser(u); setName(u.name); })
            .catch(() => navigate("/login"))
            .finally(() => setLoading(false));

        return () => window.removeEventListener("resize", checkScreenSize);
    }, [navigate]);

    const handleSave = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try {
            const updated = await authService.updateProfile({ name: name.trim() });
            setUser((prev: any) => ({ ...prev, name: updated.name }));
            showToast("success", "Profile updated successfully!");
        } catch (err: any) {
            showToast("error", err.message || "Failed to update profile");
        } finally {
            setSaving(false);
        }
    };

    const subscriptionBadge = () => {
        const s = user?.subscriptionStatus;
        if (s === "active") return { label: "Standard Pro", cls: "bg-emerald-50 text-emerald-700 border border-emerald-100" };
        if (s === "trial") return { label: "Free Trial", cls: "bg-indigo-50 text-indigo-700 border border-indigo-100" };
        return { label: s || "Free Plan", cls: "bg-slate-100 text-slate-500 border border-slate-200" };
    };

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-slate-50">
                <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
            </div>
        );
    }

    const badge = subscriptionBadge();

    return (
        <div className="flex h-screen bg-slate-50 text-slate-900 font-sans">
            <Sidebar isOpen={sidebarOpen} isMobile={isMobile} user={user} onToggle={() => setSidebarOpen(!sidebarOpen)} activePage="profile" />
            <div className="flex-1 flex flex-col overflow-hidden relative z-10">
                <Header
                    isMobile={isMobile}
                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                    user={user}
                    onLogout={() => { authService.logout(); navigate("/login"); }}
                    title="My Profile"
                />
                <ToastContainer />
                <main className="flex-1 overflow-y-auto p-6 lg:p-10 custom-scrollbar">
                    <div className="max-w-3xl mx-auto space-y-10">

                        <div className="mb-4">
                            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                                Personal Profile
                            </h2>
                            <p className="text-slate-500 text-sm mt-1">Manage your account and studio identity.</p>
                        </div>

                        {/* Profile Header */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white border border-slate-200 rounded-3xl p-8 lg:p-10 shadow-sm flex flex-col sm:flex-row items-center sm:items-start gap-8 relative overflow-hidden"
                        >
                            <div className="w-24 h-24 rounded-3xl bg-indigo-600 text-white flex items-center justify-center text-3xl font-bold shadow-xl shadow-indigo-100 shrink-0">
                                {user?.name?.charAt(0)?.toUpperCase() || "U"}
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <h3 className="text-2xl font-bold text-slate-900 mb-1">{user?.name}</h3>
                                <div className="flex items-center justify-center sm:justify-start gap-2 text-slate-500 text-sm font-medium mb-6">
                                    <Mail size={14} />
                                    <span>{user?.email}</span>
                                </div>
                                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${badge.cls}`}>
                                        <CreditCard size={12} /> {badge.label}
                                    </span>
                                    {user?.provider === "google" && (
                                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-50 border border-slate-200 text-slate-600">
                                            <Chrome size={12} /> Verified
                                        </span>
                                    )}
                                    {user?.role === "admin" && (
                                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-700">
                                            <Shield size={12} /> Admin
                                        </span>
                                    )}
                                </div>
                            </div>
                        </motion.div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Edit Profile */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="bg-white border border-slate-200 rounded-3xl p-8 lg:p-10 flex flex-col"
                            >
                                <div className="mb-8">
                                    <h3 className="text-lg font-bold text-slate-900 mb-1">General Settings</h3>
                                    <p className="text-xs text-slate-500 font-medium">Update your public information.</p>
                                </div>
                                <div className="space-y-4 mt-auto">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-700 ml-1">Full Name</label>
                                        <Input
                                            value={name}
                                            onChange={e => setName(e.target.value)}
                                            onKeyDown={e => e.key === "Enter" && handleSave()}
                                            className="h-12 bg-white border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/10 focus:border-indigo-500 text-sm font-bold"
                                            placeholder="Your full name"
                                        />
                                    </div>
                                    <Button
                                        onClick={handleSave}
                                        disabled={saving || !name.trim() || name.trim() === user?.name}
                                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 font-bold transition-all shadow-md shadow-indigo-100 disabled:opacity-50"
                                    >
                                        {saving ? <Loader2 size={18} className="animate-spin" /> : "Save Changes"}
                                    </Button>
                                </div>
                            </motion.div>

                            {/* Account Metadata */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="bg-white border border-slate-200 rounded-3xl p-8 lg:p-10"
                            >
                                <h3 className="text-lg font-bold text-slate-900 mb-8">Account Details</h3>
                                <div className="space-y-6">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Email Address</span>
                                        <span className="text-slate-900 font-bold text-sm">{user?.email}</span>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Joined Date</span>
                                        <span className="text-slate-900 font-bold text-sm">
                                            {user?.createdAt ? new Date(user.createdAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "—"}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Active Plan</span>
                                            <span className={`inline-flex items-center w-fit gap-2 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${badge.cls}`}>{badge.label}</span>
                                        </div>
                                        <Button variant="ghost" onClick={() => navigate("/billing")} className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 text-[11px] font-bold h-9 px-4 rounded-lg">
                                            Subscription →
                                        </Button>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Quick Actions */}
                        <div className="flex flex-col sm:flex-row gap-4 pt-10">
                            <Button variant="outline" onClick={() => navigate("/settings")} className="flex-1 h-12 rounded-xl border-slate-200 bg-white text-slate-700 font-bold hover:bg-slate-50 transition-all">
                                Security Settings
                            </Button>
                            <Button variant="outline" onClick={() => navigate("/billing")} className="flex-1 h-12 rounded-xl border-slate-200 bg-white text-slate-700 font-bold hover:bg-slate-50 transition-all">
                                Billing & Plan
                            </Button>
                        </div>
                    </div>
                </main>
            </div>

            {/* Toast */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className={`fixed bottom-8 right-8 z-[100] flex items-center gap-4 px-6 py-4 rounded-2xl shadow-xl border ${toast.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-rose-50 border-rose-100 text-rose-700"}`}
                    >
                        {toast.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                        <span className="text-sm font-bold">{toast.msg}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Profile;
